import { carriesGifts, DEPENDENTS_READ_TIMEOUT_MS, type GiftDependent } from 'pb-billing-ui/gift-dependents.ts';
import { licenseGiftsService } from '~/services/license-gifts-service';
import type { License } from '~/types/person/person';
import { reportFailure } from '~/utils/report-failure';

/** What a step's status region says while it reads who depends on the member's plan. */
export const DEPENDENTS_READING = 'Loading the people you protect';

/**
 * The people whose protection depends on the signed-in member's subscription, as app-api lists
 * them (`dependents`): everyone it protects now, the first to accept first.
 *
 * Read when the member is about to end that subscription, never before: the answer is the state
 * of their gifts at the moment they decide.
 *
 * Each call to this composable is one flow: a step that reads the people again on the member's
 * next press or its own Try again keeps one instance for its life, so the failures it counts are
 * that step's own.
 */
export const useGiftDependents = () => {
    const authStore = useAuthStore();

    // Seats whose last read failed. The first failure is said so the member can try again; the
    // next one in a row is the flow giving up on the names.
    const failedOnce = new Set<number>();

    /**
     * Whether ending this seat ends the signed-in member's gifts: a live Pro or Signature seat they
     * hold. A seat they bought for someone else carries that person's gifts, not theirs.
     */
    function sponsorsDependents(license: License): boolean {
        return carriesGifts(license, authStore.user?.identity?.id);
    }

    /**
     * Who loses their protection if this seat ends, read now; none for a seat that carries no gifts.
     * A read that has not landed within `DEPENDENTS_READ_TIMEOUT_MS` has failed.
     *
     * Null when the read failed, which the step says (`pb-billing-ui`'s `DEPENDENTS_UNREAD`) before
     * the member tries again. A read that fails again right after answers none, and is reported
     * rather than shown: ending the subscription stays the member's decision, so the step goes on
     * without the names instead of holding the member at the failure.
     */
    async function dependentsOn(license: License): Promise<GiftDependent[] | null> {
        if (!sponsorsDependents(license)) return [];

        try {
            const { dependents } = await licenseGiftsService.overview({ timeout: DEPENDENTS_READ_TIMEOUT_MS });
            failedOnce.delete(license.id);
            return dependents;
        } catch (error: unknown) {
            if (!failedOnce.has(license.id)) {
                failedOnce.add(license.id);
                return null;
            }

            failedOnce.delete(license.id);
            reportFailure('Reading who depends on the subscription', error);
            return [];
        }
    }

    return { sponsorsDependents, dependentsOn };
};
